<template>
  <div class="module-page pa-10">

    <!-- ── Header ── -->
    <div class="mb-8">
      <div class="page-title">Switch Role</div>
      <div class="page-sub mt-1">Select the role you want to use for this session</div>
    </div>

    <!-- ── Loading skeleton ── -->
    <v-row v-if="loading">
      <v-col v-for="n in 3" :key="n" cols="12" sm="6" md="4" lg="3">
        <v-skeleton-loader type="card" rounded="xl" />
      </v-col>
    </v-row>

    <!-- ── Role cards ── -->
    <v-row v-else-if="roles.length">
      <v-col
        v-for="(role, idx) in roles"
        :key="role.id"
        cols="12" sm="6" md="4" lg="3"
      >
        <div
          class="role-card rounded-xl pa-6"
          :class="activeRole === role.id ? 'role-card--active' : 'role-card--idle'"
          @click="selectRole(role.id)"
        >
          <!-- Icon badge -->
          <div
            class="role-icon-box rounded-lg mb-5"
            :style="{ backgroundColor: palette(idx).box }"
          >
            <v-icon size="26" :color="palette(idx).icon">{{ palette(idx).mdi }}</v-icon>
          </div>

          <!-- Role name -->
          <div class="role-name mb-2">{{ role.description }}</div>

          <!-- Status chip -->
          <v-chip
            v-if="activeRole === role.id"
            color="#10b981"
            size="small"
            variant="flat"
            prepend-icon="mdi-check-circle-outline"
            class="text-white chip-active"
          >
            Active
          </v-chip>
          <span v-else class="role-hint">Click to activate</span>

          <!-- Active indicator bar -->
          <div v-if="activeRole === role.id" class="active-bar" />
        </div>
      </v-col>
    </v-row>

    <!-- ── Empty state ── -->
    <div v-else class="empty-state">
      <v-icon size="64" color="#cbd5e1">mdi-account-multiple-outline</v-icon>
      <div class="empty-title mt-4">No roles assigned</div>
      <div class="empty-sub mt-1">Contact your administrator to get roles assigned.</div>
    </div>

  </div>
</template>

<script>
const PALETTE = [
  { box: "#ecfdf5", icon: "#059669", mdi: "mdi-account-tie-outline" },
  { box: "#eff6ff", icon: "#2563eb", mdi: "mdi-shield-account-outline" },
  { box: "#fef3c7", icon: "#d97706", mdi: "mdi-account-star-outline" },
  { box: "#f5f3ff", icon: "#7c3aed", mdi: "mdi-account-badge-outline" },
  { box: "#fff1f2", icon: "#e11d48", mdi: "mdi-account-supervisor-outline" },
];

export default {
  created() {
    if (this.$store.state.expiryDate < Date.now()) {
      this.$store.dispatch("setUser", null);
      this.$store.dispatch("setIsAuthenticated", 0);
      this.$router.push("/");
    }
    this.activeRole = this.$store.state.activeRole;
    this.getRoles();
  },
  data: () => ({
    roles: [],
    activeRole: null,
    loading: true,
  }),
  methods: {
    getRoles() {
      this.axiosCall("/user-role/getMyRoles", "GET").then((res) => {
        this.roles = res.data;
        this.loading = false;
      });
    },
    selectRole(id) {
      this.activeRole = id;
      this.$store.dispatch("setActiveRole", id);
    },
    palette(idx) {
      return PALETTE[idx % PALETTE.length];
    },
  },
};
</script>

<style scoped>
/* ── Page header ── */
.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.4px;
}

.page-sub {
  font-size: 14px;
  color: #64748b;
}

/* ── Role card ── */
.role-card {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  user-select: none;
}

.role-card--idle {
  background: #ffffff;
  border-color: #e2e8f0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.role-card--idle:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  border-color: #a7f3d0;
}

.role-card--active {
  background: #f0fdf4;
  border-color: #10b981;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.15);
}

/* ── Icon box ── */
.role-icon-box {
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Text ── */
.role-name {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.4;
}

.role-hint {
  font-size: 12px;
  color: #94a3b8;
}

.chip-active {
  font-size: 11px;
  font-weight: 600;
}

/* ── Active bottom bar ── */
.active-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: #10b981;
  border-radius: 0 0 10px 10px;
}

/* ── Empty state ── */
.empty-state {
  text-align: center;
  padding: 80px 0;
}

.empty-title {
  font-size: 18px;
  font-weight: 600;
  color: #475569;
}

.empty-sub {
  font-size: 14px;
  color: #94a3b8;
}
</style>
